import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSignupStore, SignupStep } from "@/lib/store/use-signup-store";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import {
  RequiredTermStep,
  SignupFormStep,
  PersonalSmsStep,
  ParentRequiredTermStep,
  ParentInfoInputStep,
  ParentKmcVerificationStep,
  Under14SmsStep,
} from "./_components/steps";

// Step 컴포넌트 매핑 (RequiredTermStep만 onExit prop 사용)
function getStepComponent(step: SignupStep, onExit: () => void) {
  switch (step) {
    case "required-term":
      return <RequiredTermStep onExit={onExit} />;
    case "signup-form":
      return <SignupFormStep />;
    case "personal-sms":
      return <PersonalSmsStep />;
    case "parent-required-term":
      return <ParentRequiredTermStep />;
    case "parent-info-input":
      return <ParentInfoInputStep />;
    case "parent-kmc-verification":
      return <ParentKmcVerificationStep />;
    case "under-14-sms":
      return <Under14SmsStep />;
    default:
      return <RequiredTermStep onExit={onExit} />;
  }
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { currentStep, goToPrevStep, clearSignup } = useSignupStore();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  // 회원가입 이탈 확인
  const handleExit = useCallback(() => {
    setAlertDialogState({
      title: "회원가입을 중단하시겠어요?",
      description: "지금까지 입력한 정보는 저장되지 않습니다.",
      confirmText: "나가기",
      cancelText: "계속하기",
      onConfirm: () => {
        clearSignup();
        resetAlertDialogState();
        navigate("/auth/login", { replace: true });
      },
      onCancel: () => {
        resetAlertDialogState();
      },
    });
  }, [clearSignup, resetAlertDialogState, navigate, setAlertDialogState]);

  // 브라우저 뒤로가기 핸들링
  useEffect(() => {
    // 초기 history state 설정
    window.history.pushState({ step: currentStep }, "");

    const handlePopState = () => {
      // 첫 번째 step이면 이탈 확인
      if (currentStep === "required-term") {
        handleExit();
      } else {
        goToPrevStep();
      }
      // 다시 history에 현재 상태 push하여 뒤로가기 방지
      window.history.pushState({ step: currentStep }, "");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [currentStep, goToPrevStep, handleExit]);

  return getStepComponent(currentStep, handleExit);
}
